import type { ApiResponse } from '@/types/api'
import { ApiClientError } from '@/lib/api/client'
import { beginPublicRequest, blockPublicRequests } from './public-request-budget'
import {
  buildLandingApiUrl,
  canReplayLandingRequestBody,
  getLandingApiBaseUrls,
  getLandingApiGenericErrorMessage,
  isLandingApiProxyMiss,
  rememberLandingApiProxyMiss,
} from '@/lib/landing-api-bases'

type LandingRequestOptions = RequestInit & {
  token?: string | null
}

async function parseLandingApiResponse<T>(
  response: Response
): Promise<ApiResponse<T> | null> {
  if (response.status === 204) {
    return { success: true } as ApiResponse<T>
  }

  let raw: string
  try {
    raw = await response.text()
  } catch {
    return null
  }

  const trimmed = raw.replace(/^\uFEFF/, '').trim()
  if (!trimmed) {
    return null
  }

  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
    return null
  }

  try {
    return JSON.parse(trimmed) as ApiResponse<T>
  } catch {
    return null
  }
}

function resolveLandingApiData<T>(data: ApiResponse<T>): T {
  if ('data' in data) {
    return data.data as T
  }

  return { message: data.message } as T
}

export async function landingApiRequest<T>(
  endpoint: string,
  options: LandingRequestOptions = {}
): Promise<T> {
  const finish = beginPublicRequest(endpoint, options.method)
  try {
    return await executeLandingApiRequest<T>(endpoint, options)
  } finally {
    finish()
  }
}

async function executeLandingApiRequest<T>(
  endpoint: string,
  options: LandingRequestOptions
): Promise<T> {
  const fetchOptions: RequestInit = { ...options }
  delete (fetchOptions as { token?: string | null }).token

  const isFormData =
    typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData
  const headers: Record<string, string> = {
    Accept: 'application/json',
  }

  if (!isFormData) {
    headers['Content-Type'] = 'application/json'
  }

  if (fetchOptions.headers) {
    const extraHeaders = new Headers(fetchOptions.headers)
    extraHeaders.forEach((value, key) => {
      if (isFormData && key.toLowerCase() === 'content-type') return
      if (key.toLowerCase() === 'authorization') return
      headers[key] = value
    })
  }

  const bases = canReplayLandingRequestBody(fetchOptions.body)
    ? getLandingApiBaseUrls()
    : getLandingApiBaseUrls().slice(0, 1)
  let lastError: unknown = null

  for (let index = 0; index < bases.length; index += 1) {
    const baseUrl = bases[index]
    const hasMore = index < bases.length - 1

    try {
      const response = await fetch(buildLandingApiUrl(endpoint, baseUrl), {
        ...fetchOptions,
        credentials: 'include',
        cache: 'no-store',
        referrerPolicy: 'strict-origin',
        headers,
      })

      if (response.status === 429) {
        blockPublicRequests(
          endpoint,
          options.method ?? 'GET',
          response.headers.get('Retry-After')
        )
        throw new ApiClientError(
          'Muitas tentativas. Aguarde um pouco e tente novamente.',
          429
        )
      }

      if (hasMore && isLandingApiProxyMiss(response)) {
        rememberLandingApiProxyMiss()
        lastError = new ApiClientError(
          getLandingApiGenericErrorMessage(),
          response.status
        )
        continue
      }

      const data = await parseLandingApiResponse<T>(response)
      if (data === null) {
        if (hasMore) {
          rememberLandingApiProxyMiss()
          lastError = new ApiClientError(
            getLandingApiGenericErrorMessage(),
            response.status
          )
          continue
        }

        throw new ApiClientError(getLandingApiGenericErrorMessage(), response.status)
      }

      if (!response.ok) {
        if (response.status === 422) {
          throw new ApiClientError(
            data.message || 'Erro de validação',
            response.status,
            data.errors
          )
        }

        throw new ApiClientError(
          data.message || getLandingApiGenericErrorMessage(),
          response.status,
          data.errors
        )
      }

      if (data.success === false) {
        throw new ApiClientError(
          data.message || 'Erro na requisição',
          response.status,
          data.errors
        )
      }

      return resolveLandingApiData(data)
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw error
      }

      if (hasMore) {
        rememberLandingApiProxyMiss()
        lastError = error
        continue
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiClientError(
          'Erro de conexão. Verifique sua internet e tente novamente.',
          0
        )
      }

      throw new ApiClientError(getLandingApiGenericErrorMessage(), 0)
    }
  }

  if (lastError instanceof ApiClientError) {
    throw lastError
  }
  if (lastError instanceof TypeError && lastError.message.includes('fetch')) {
    throw new ApiClientError(
      'Erro de conexão. Verifique sua internet e tente novamente.',
      0
    )
  }

  throw new ApiClientError(getLandingApiGenericErrorMessage(), 0)
}
